'use client';

import { useEffect, useState } from 'react';
import { mediaUrl } from '@/utils/mediaUrl';

/**
 * An <img> that degrades to a placeholder instead of a broken-image icon.
 *
 * WHY THIS EXISTS: a large number of stored image URLs point at a media host
 * that no longer resolves (the old img.gurukul365.com). Every one of those
 * rendered as the browser's broken-image glyph — or, worse, as a blank gap in
 * a card that still reserved space for it. Roughly a hundred raw <img> tags
 * across the portal each needed their own onError handler to avoid that, and
 * most did not have one. Those files still exist at the same path on the
 * live host, so `mediaUrl` rewrites the hostname first and the placeholder
 * is only reached when the file is genuinely gone.
 *
 * This owns the behaviour once:
 *   • no src at all              -> placeholder, no network request
 *   • src that fails to load     -> placeholder, exactly once (no retry loop)
 *   • src changes later          -> error state resets, so a repaired URL works
 *
 * The `variant` picks what "placeholder" means, because a missing student
 * photo and a missing school logo should not look the same.
 *
 *   avatar  — a person. Initials when a `name` is given, else a person glyph.
 *   logo    — a brand mark. Falls back to the Kaympus logo.
 *   generic — anything else (a banner, a document scan): a neutral tile.
 *
 * NOTE: deliberately a plain <img>, not next/image. These are arbitrary
 * remote hosts on a multi-tenant deployment; next/image would demand every
 * tenant's media domain be whitelisted in next.config, which is exactly the
 * kind of coupling that breaks when the media host changes.
 */

const initialsOf = (name = '') =>
  String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

function PersonGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
      <circle cx="12" cy="8.5" r="3.75" fill="currentColor" opacity="0.45" />
      <path d="M3.5 21c0-4.2 3.8-7 8.5-7s8.5 2.8 8.5 7" fill="currentColor" opacity="0.45" />
    </svg>
  );
}

function ImageGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.6" opacity="0.5" />
      <circle cx="8.75" cy="10" r="1.6" fill="currentColor" opacity="0.5" />
      <path d="M4.5 17.5 9.5 13l3.2 3 2.6-2.2 4.2 3.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
    </svg>
  );
}

export default function SafeImage({
  src,
  alt = '',
  name = '',
  variant = 'generic',
  className = '',
  imgClassName = '',
  placeholderClassName = '',
  fallbackSrc,
  ...rest
}) {
  const [failed, setFailed] = useState(false);
  const resolvedSrc = mediaUrl(src);

  // A repaired or swapped URL must get a fresh chance — without this the
  // component stays stuck on the placeholder for the rest of its life.
  useEffect(() => { setFailed(false); }, [resolvedSrc]);

  const usable = typeof resolvedSrc === 'string' && resolvedSrc.trim() !== '';

  if (usable && !failed) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={resolvedSrc}
        alt={alt}
        className={`${className} ${imgClassName}`.trim()}
        // Once, not in a loop: flipping to state rather than reassigning
        // e.currentTarget.src means a failing placeholder can't re-trigger us.
        onError={() => setFailed(true)}
        {...rest}
      />
    );
  }

  // An explicit fallback image wins over the drawn placeholders.
  if (fallbackSrc) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={fallbackSrc} alt={alt} className={`${className} ${imgClassName}`.trim()} {...rest} />
    );
  }

  if (variant === 'logo') {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src="/logo.png" alt={alt} className={`${className} ${imgClassName}`.trim()} {...rest} />
    );
  }

  const initials = variant === 'avatar' ? initialsOf(name) : '';
  // Order matters: the default tint goes FIRST so a caller's own bg-*/text-*
  // wins by Tailwind's later-in-source rule (e.g. a dark profile header
  // passing its own tile colours), and placeholderClassName goes last so it
  // can override both without touching the <img> path.
  return (
    <span
      role="img"
      aria-label={alt || (variant === 'avatar' ? name || 'No photo' : 'No image')}
      className={`bg-gray-100 text-gray-400 inline-flex items-center justify-center overflow-hidden select-none ${className} ${placeholderClassName}`.trim()}
      // Deliberately NOT spreading {...rest} here: img-only attributes like
      // width/height/loading are invalid on a span, and a click handler would
      // land on a non-focusable element. Only data-testid is carried across so
      // tests can still find the slot in its failed state.
      data-testid={rest['data-testid']}
    >
      {initials ? (
        <span className="font-semibold leading-none" style={{ fontSize: '0.9em' }}>{initials}</span>
      ) : variant === 'avatar' ? (
        <PersonGlyph className="w-3/5 h-3/5" />
      ) : (
        <ImageGlyph className="w-1/2 h-1/2" />
      )}
    </span>
  );
}
